import React, { useEffect, useRef, useState } from "react";
import CurrencyConverter from "@heartinz/currency_subunit_converter";
import Messages from "../../../constants/Messages";
import { useShowAlert } from "../../../context/AlertContext";
import { CheckIcon, CloseIcon } from "../../../constants/Icons";

const ChangeModal = ({ changeAmount, onClose, onConfirm }) => {
  const showAlert = useShowAlert();
  const cashRef = useRef(null);
  const onlineRef = useRef(null);
  const paylaterRef = useRef(null);
  const wavedoffRef = useRef(null);
  const buttonRef = useRef(null);
  const [changeamount, setChangeAmount] = useState(
    CurrencyConverter.toCurrency(changeAmount || 0, "INR") * -1
  );
  const [cashchange, setCashChange] = useState(0);
  const [upichange, setUpiChange] = useState(0);
  const [paylaterchange, setPayLaterChange] = useState(0);
  const [wavedoffchange, setWavedOffChange] = useState(0);

  const handleInputChange = (setValue, field) => (e) => {
    const value = e.target.value;
    const regex = /^\d*\.?\d{0,2}$/;
    if (!regex.test(value)) {
      setValue(0);
      return;
    }
    const currentValues = {
      cashchange: field === "cash" ? 0 : parseFloat(cashchange || 0),
      upichange: field === "upi" ? 0 : parseFloat(upichange || 0),
      paylaterchange:
        field === "paylater" ? 0 : parseFloat(paylaterchange || 0),
      wavedoffchange:
        field === "wavedoff" ? 0 : parseFloat(wavedoffchange || 0),
    };
    const newValue = parseFloat(value || 0);
    const totalChange =
      currentValues.cashchange +
      currentValues.upichange +
      currentValues.paylaterchange +
      currentValues.wavedoffchange +
      newValue;

    if (totalChange > changeamount) {
      showAlert("warning", Messages.ERROR.CHANGE_EXCEEDS_AMOUNT);
      return;
    }
    setValue(value);
  };

  const handleConfirm = () => {
    const cash = parseFloat(cashchange || 0);
    const upi = parseFloat(upichange || 0);
    const paylater = parseFloat(paylaterchange || 0);
    const wavedoff = parseFloat(wavedoffchange || 0);
    const totalchange = cash + upi + paylater + wavedoff;

    if (totalchange !== changeamount) {
      cashRef.current.focus();
      showAlert("warning", Messages.ERROR.INVALID_CHANGE_AMOUNT);
      return;
    }

    const changeData = {
      changeamount: changeamount,
      cashchange: cash,
      upichange: upi,
      paylaterchange: paylater,
      wavedoffchange: wavedoff,
    };
    onConfirm(changeData);
    onClose();
  };

  const handleFocusFill = (mode) => {
    const currentCash = Number(cashchange || 0);
    const currentUpi = Number(upichange || 0);
    const currentPaylater = Number(paylaterchange || 0);
    const currentWavedoff = Number(wavedoffchange || 0);

    const totalAlreadyPaid =
      currentCash + currentUpi + currentPaylater + currentWavedoff;

    const remainingBalance = changeamount - totalAlreadyPaid;
    if (remainingBalance <= 0) return;

    // Set other fields to 0 if they’re empty
    if (!cashchange) setCashChange(0);
    if (!upichange) setUpiChange(0);
    if (!paylaterchange) setPayLaterChange(0);
    if (!wavedoffchange) setWavedOffChange(0);

    if (mode === "cash") {
      setCashChange(currentCash + remainingBalance);
      setTimeout(() => cashRef?.current?.select(), 0);
    } else if (mode === "upi") {
      setUpiChange(currentUpi + remainingBalance);
      setTimeout(() => onlineRef?.current?.select(), 0);
    } else if (mode === "paylater") {
      setPayLaterChange(currentPaylater + remainingBalance);
      setTimeout(() => paylaterRef?.current?.select(), 0);
    } else if (mode === "wavedoff") {
      setWavedOffChange(currentWavedoff + remainingBalance);
      setTimeout(() => wavedoffRef?.current?.select(), 0);
    }
  };

  const handleEnterFocus = (e, nextRef) => {
    if (e.key === "Enter") {
      e.preventDefault();
      nextRef?.current?.focus();
    }
  };

  const handlePaymodeKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleConfirm(e);
    }
  };

  const totalChange =
    parseFloat(cashchange || 0) +
    parseFloat(upichange || 0) +
    parseFloat(paylaterchange || 0) +
    parseFloat(wavedoffchange || 0);
  const isTotalChangeValid = totalChange === changeamount;

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleEsc);

    // Cleanup on unmount
    return () => {
      window.removeEventListener("keydown", handleEsc);
    };
  }, [onClose]);

  return (
    <div>
      <div className="modal-backdrop show"></div>
      <div
        className="modal show"
        id="CustomerModal"
        tabIndex="0"
        aria-modal="true"
        role="dialog"
        style={{ display: "block" }}
      >
        <div className="modal-dialog modal-dialog-scrollable modal-dialog-centered modal-sm">
          <div className="modal-content">
            <div className="modal-header">
              <h6 className="modal-title"> How Did You Pay Change? </h6>
              <button
                type="button"
                onClick={onClose}
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <div className="rounded mb-0">
                <div
                  className={`rounded p-2 text-center mb-3 ${
                    isTotalChangeValid
                      ? "bg-light-success text-success"
                      : "bg-light text-dark"
                  }`}
                >
                  <div className="mb-0 fs-15 fw-medium">
                    Total Change: <span> {changeamount} </span>
                  </div>
                </div>
                <div className="flex-grow-1 row justify-content-between align-items-center g-2">
                  <div className="col-6">
                    <h6 className="mb-0">Cash</h6>
                  </div>
                  <div className="col-6 text-end">
                    <input
                      ref={cashRef}
                      type="text"
                      className="form-control"
                      min="0"
                      value={cashchange}
                      onClick={() => handleFocusFill("cash")}
                      onKeyDown={(e) => handleEnterFocus(e, onlineRef)}
                      onFocus={(e) => e.target.select()}
                      onChange={handleInputChange(setCashChange, "cash")}
                      autoFocus
                    />
                  </div>
                  <div className="col-6">
                    <h6 className="mb-0">UPI</h6>
                  </div>
                  <div className="col-6 text-end">
                    <input
                      ref={onlineRef}
                      type="text"
                      className="form-control"
                      min="0"
                      value={upichange}
                      onClick={() => handleFocusFill("upi")}
                      onKeyDown={(e) => handleEnterFocus(e, paylaterRef)}
                      onFocus={(e) => e.target.select()}
                      onChange={handleInputChange(setUpiChange, "upi")}
                    />
                  </div>
                  <div className="col-6">
                    <h6 className="mb-0 text-brown">
                      Pay Later <small>(Credit)</small>
                    </h6>
                  </div>
                  <div className="col-6 text-end">
                    <input
                      ref={paylaterRef}
                      type="text"
                      className="form-control"
                      min="0"
                      value={paylaterchange}
                      onClick={() => handleFocusFill("paylater")}
                      onKeyDown={(e) => handleEnterFocus(e, wavedoffRef)}
                      onFocus={(e) => e.target.select()}
                      onChange={handleInputChange(
                        setPayLaterChange,
                        "paylater"
                      )}
                    />
                  </div>
                  <div className="col-6">
                    <h6 className="mb-0 text-info">Waved Off</h6>
                  </div>
                  <div className="col-6 text-end">
                    <input
                      ref={wavedoffRef}
                      type="text"
                      className="form-control"
                      min="0"
                      value={wavedoffchange}
                      onKeyDown={(e) => handlePaymodeKeyDown(e)}
                      onClick={() => handleFocusFill("wavedoff")}
                      onFocus={(e) => e.target.select()}
                      onChange={handleInputChange(
                        setWavedOffChange,
                        "wavedoff"
                      )}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer d-block">
              <button
                onClick={onClose}
                type="button"
                data-bs-dismiss="modal"
                className="btn btn-outline-secondary text-left"
              >
                <CloseIcon iClass={"fs-15"}/> Cancel
              </button>
              <button
                ref={buttonRef}
                type="button"
                className="btn btn-primary text-right"
                onClick={handleConfirm}
              >
                <CheckIcon/> Ok
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangeModal;
